import React, { useMemo, useState } from 'react';
import { Card, Badge, Row, Col, Spinner, Form, Button } from 'react-bootstrap';
import { useQuery, useQueries } from '@tanstack/react-query';
import { http } from '../../../features/jobs/api/http';

// At most one decimal, on purpose. A reel with 13.508 m left printed itself as
// "13.508 m available" next to figures like "5,251.921" — and read as thirteen
// THOUSAND metres, which is how a reel with 13 m on it looked like the biggest
// one on the floor. One decimal cannot be mistaken for a thousands group in
// either locale.
const fmt = n =>
  Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 1 });

async function fetchCompatible(params) {
  const { data } = await http.get('/api/redis/phantoms/compatible', { params });
  return data.phantoms || [];
}

// check if a phantom's wire specs are compatible with ANY of the product's conductors
function isWireCompatible(phantom, wireSpecs) {
  if (!wireSpecs || !wireSpecs.length) return true;

  const pType = String(phantom.wireType || '').toUpperCase();

  const pDiams = Array.isArray(phantom.wireDiameter)
    ? phantom.wireDiameter.map(Number).sort((a, b) => a - b)
    : phantom.wireDiameter != null
      ? [Number(phantom.wireDiameter)]
      : [];

  const pCounts = Array.isArray(phantom.numberOfWires)
    ? phantom.numberOfWires.map(Number).sort((a, b) => a - b)
    : phantom.numberOfWires != null
      ? [Number(phantom.numberOfWires)]
      : [];

  for (const spec of wireSpecs) {
    const sType = String(spec.wireType || '').toUpperCase();
    if (pType !== sType) continue;

    const sDiams = Array.isArray(spec.wireDiameter)
      ? spec.wireDiameter.map(Number).sort((a, b) => a - b)
      : spec.wireDiameter != null
        ? [Number(spec.wireDiameter)]
        : [];

    if (pDiams.length !== sDiams.length) continue;
    const diamsMatch = pDiams.every((d, i) => Math.abs(d - sDiams[i]) < 0.01);
    if (!diamsMatch) continue;

    if (pCounts.length && spec.numberOfWires) {
      const sCounts = Array.isArray(spec.numberOfWires)
        ? spec.numberOfWires.map(Number).sort((a, b) => a - b)
        : [Number(spec.numberOfWires)];
      if (pCounts.length !== sCounts.length) continue;
      const countsMatch = pCounts.every((c, i) => c === sCounts[i]);
      if (!countsMatch) continue;
    }

    return true;
  }

  return false;
}

// ── Single-select phantom card (original behavior for unwinding, conductor_isolation) ──
const SingleSelectCard = ({ phantom, onUsePhantom }) => {
  const wType = String(phantom.wireType || '').toUpperCase();
  const wDiam = Array.isArray(phantom.wireDiameter)
    ? phantom.wireDiameter.join(', ')
    : phantom.wireDiameter || '';
  const wCount = Array.isArray(phantom.numberOfWires)
    ? phantom.numberOfWires.join(', ')
    : phantom.numberOfWires || '';
  const showAvail = phantom.effectiveAvailable ?? phantom.availableLength;

  return (
    <div
      className="p-2 rounded border border-info bg-white"
      style={{ cursor: 'pointer', fontSize: 12 }}
      onClick={() => onUsePhantom && onUsePhantom({ ...phantom, availableLength: showAvail })}
    >
      <div className="d-flex justify-content-between align-items-center mb-1">
        <Badge bg="info" style={{ fontSize: 10 }}>PHANTOM</Badge>
        <span className="fw-bold">{fmt(showAvail)} m</span>
      </div>
      <div className="text-muted" style={{ fontSize: 11 }}>
        {phantom.color && <Badge bg="dark" className="me-1">{phantom.color}</Badge>}
        {wType && <span className="me-1">{wType}</span>}
        {wDiam && <span className="me-1">Ø{wDiam}</span>}
        {wCount && <span className="me-1">×{wCount}</span>}
      </div>
      {phantom.drum_material && (
        <div className="text-muted" style={{ fontSize: 10 }}>
          {phantom.drum_material} {phantom.drum_diameter && `Ø${phantom.drum_diameter}`}
        </div>
      )}
      {phantom.note && (
        <div className="text-muted mt-1" style={{ fontSize: 10, fontStyle: 'italic' }}>
          {phantom.note}
        </div>
      )}
    </div>
  );
};

// ── Multi-select phantom card for pre_stranding (stock reels) ──
//
// `ropeSpecs` present = a cable stranded twice (a reduced earth). Then every
// figure here is PER ROPE: the 35 mm² rope needs `length × cores` and the
// 16 mm² earth needs `length`, and a reel of one is no use to the other. One
// picker still handles both, because confirming replaces every phantom
// execution on the phase at once — two pickers would each wipe the other's.
const MultiSelectPicker = ({
  available,
  totalNeeded,
  onUsePhantoms,
  usedPhantomIds,
  ropeSpecs = null,
  // What THIS phase has already committed, per reel. Confirming replaces every
  // phantom execution on the phase at once, so the committed amount is shown as
  // a live selection rather than subtracted away: otherwise a planner who took
  // 5 150 m of a 5 163.5 m reel saw a green SELECTED card reading "13.5 m
  // available" and no way to correct the figure downwards.
  ownAlloc = {},
  takenBy = {}
}) => {
  const [selections, setSelections] = useState({});

  // Re-seeded whenever the phase's own commitment changes — including after a
  // confirm, which is what stops the badge from going stale.
  const ownKey = JSON.stringify(ownAlloc);
  React.useEffect(() => {
    const seeded = {};
    for (const [pid, m] of Object.entries(ownAlloc || {})) {
      if (Number(m) > 0) seeded[pid] = Number(m);
    }
    setSelections(seeded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownKey]);

  const groups = useMemo(() => {
    const perRope = (ropeSpecs || []).filter(s => s && s.ropeIndex != null);
    if (!perRope.length) {
      return [
        {
          key: 'all',
          ropeIndex: null,
          label: null,
          // The specs know; `totalNeeded` is the fallback for a phase that has
          // no rope list at all.
          needed: Number(ropeSpecs?.[0]?.needed) || totalNeeded,
          reels: available
        }
      ];
    }
    return perRope.map(spec => ({
      key: `rope-${spec.ropeIndex}`,
      ropeIndex: spec.ropeIndex,
      label: spec.label,
      structure: spec.structure,
      needed: Number(spec.needed) || 0,
      reels: available.filter(p => p.ropeIndex === spec.ropeIndex)
    }));
  }, [ropeSpecs, available, totalNeeded]);

  /** Selected metres, counted only within one rope's reels. */
  const selectedFor = (group, sel = selections) =>
    group.reels.reduce((s, p) => s + (Number(sel[p.phantomId]) || 0), 0);

  const togglePhantom = (phantomId, maxLen) => {
    setSelections(prev => {
      if (prev[phantomId] !== undefined) {
        const next = { ...prev };
        delete next[phantomId];
        return next;
      }
      // Default to what THIS rope still needs. Counting the whole phase's
      // selection here would offer the earth nothing once the main rope was
      // covered.
      const group =
        groups.find(g => g.reels.some(p => p.phantomId === phantomId)) || groups[0];
      const stillNeeded = Math.max(0, (group?.needed || 0) - selectedFor(group, prev));
      return { ...prev, [phantomId]: Math.min(maxLen, stillNeeded) };
    });
  };

  const updateAmount = (phantomId, val) => {
    setSelections(prev => ({ ...prev, [phantomId]: Math.max(0, Number(val) || 0) }));
  };

  const totalSelected = (sel) =>
    Object.values(sel || selections).reduce((s, v) => s + (Number(v) || 0), 0);

  const selected = totalSelected();
  const canConfirm = selected > 0;

  const handleConfirm = () => {
    const result = Object.entries(selections)
      .filter(([, len]) => len > 0)
      .map(([phantomId, useLength]) => {
        const ph = available.find(p => p.phantomId === phantomId);
        // A reel another product spent while this picker was open is gone from
        // `available`, and `{ ...undefined }` used to produce an execution with
        // no phantomId at all — metres from nowhere.
        if (!ph) return null;
        // Never more than the reel still holds after everyone else's claims.
        // Two pickers can be open on two products at once, and both read the
        // full reel until one of them confirms.
        const cap = Number(ph.effectiveAvailable ?? ph.availableLength) || 0;
        return { ...ph, useLength: Math.min(Number(useLength) || 0, cap) };
      })
      .filter(r => r && r.useLength > 0);
    if (result.length > 0) onUsePhantoms(result);
  };

  const multiRope = groups.length > 1;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        {multiRope ? (
          <small className="text-700">
            {groups.length} ropes on this cable — each needs its own section
          </small>
        ) : (
          <small className="text-700">
            Need: <b>{fmt(totalNeeded)} m</b> | Selected: <b className={selected >= totalNeeded ? 'text-success' : 'text-warning'}>{fmt(selected)} m</b>
            {selected < totalNeeded && <span className="text-muted ms-1">(rest will be produced)</span>}
          </small>
        )}
        {canConfirm && (
          <Button size="sm" variant="info" onClick={handleConfirm}>
            Use selected stock ({Object.keys(selections).filter(k => selections[k] > 0).length} reels)
          </Button>
        )}
      </div>

      {/* Per rope, because the two ropes of a reduced-earth cable are two
          different conductors: a reel of 16 mm² cannot go on the 35 mm² rope,
          and "Need" is a different number for each. */}
      {groups.map(group => (
        <div key={group.key} className={multiRope ? 'mb-3' : ''}>
          {multiRope && (
            <div className="d-flex justify-content-between align-items-center mb-1">
              <small className="fw-semibold">
                {group.label}
                {group.structure && <span className="text-muted ms-1">{group.structure}</span>}
              </small>
              <small className="text-700">
                Need: <b>{fmt(group.needed)} m</b> | Selected:{' '}
                <b className={selectedFor(group) >= group.needed ? 'text-success' : 'text-warning'}>
                  {fmt(selectedFor(group))} m
                </b>
              </small>
            </div>
          )}
          {multiRope && !group.reels.length && (
            <div className="text-muted fs-10 mb-1">
              No stock reel of this section — this rope will be produced.
            </div>
          )}
          <Row className="g-2">
        {group.reels.map(ph => {
          const isSelected = selections[ph.phantomId] !== undefined;
          const wType = String(ph.wireType || '').toUpperCase();
          const wDiam = Array.isArray(ph.wireDiameter) ? ph.wireDiameter.join(', ') : ph.wireDiameter || '';

          return (
            <Col xs={12} sm={6} md={4} key={ph.phantomId}>
              <div
                className={`p-2 rounded border ${isSelected ? 'border-success bg-success-subtle' : 'border-info bg-white'}`}
                style={{ fontSize: 12 }}
              >
                <div
                  className="d-flex justify-content-between align-items-center mb-1"
                  style={{ cursor: 'pointer' }}
                  onClick={() => togglePhantom(ph.phantomId, ph.effectiveAvailable ?? ph.availableLength)}
                >
                  <Badge bg={isSelected ? 'success' : 'info'} style={{ fontSize: 10 }}>
                    {isSelected ? 'SELECTED' : 'STOCK'}
                  </Badge>
                  <span className="fw-bold">{fmt(ph.effectiveAvailable ?? ph.availableLength)} m available</span>
                </div>
                <div className="text-muted mb-1" style={{ fontSize: 11 }}>
                  {wType && <span className="me-1">{wType}</span>}
                  {wDiam && <span className="me-1">Ø{wDiam}</span>}
                  sect. {ph.section}
                </div>
                {/* Who is already on this reel. One physical bobbin can feed
                    several products in one launch, and without this the planner
                    could not tell a reel nobody had touched from one with 13 m
                    left — the card looked the same either way. */}
                {(takenBy[ph.phantomId] || []).length > 0 && (
                  <div className="mb-1" style={{ fontSize: 10 }}>
                    {(takenBy[ph.phantomId] || []).map((t, i) => (
                      <div key={i} className={t.own ? 'text-info' : 'text-warning'}>
                        {fmt(t.metres)} m → {t.own ? 'this phase' : t.label}
                      </div>
                    ))}
                    <div className="text-700">
                      {fmt(ph.effectiveAvailable ?? ph.availableLength)} m left of{' '}
                      {fmt(ph.availableLength)} m
                    </div>
                  </div>
                )}
                {ph.note && (
                  <div className="text-muted mb-1" style={{ fontSize: 10, fontStyle: 'italic' }}>
                    {ph.note}
                  </div>
                )}
                {isSelected && (
                  <Form.Group className="mt-1">
                    <Form.Label className="mb-0" style={{ fontSize: 10 }}>Use (m):</Form.Label>
                    <Form.Control
                      size="sm"
                      type="number"
                      min={0}
                      max={ph.effectiveAvailable ?? ph.availableLength}
                      value={selections[ph.phantomId] || ''}
                      onChange={e => updateAmount(ph.phantomId, e.target.value)}
                      onClick={e => e.stopPropagation()}
                      style={{ fontSize: 12 }}
                    />
                  </Form.Group>
                )}
              </div>
            </Col>
          );
        })}
          </Row>
        </div>
      ))}
    </>
  );
};

const PhantomTokenPicker = ({
  phaseName,
  section,
  wireSpecs,
  phaseData,
  totalNeeded,
  // One spec per rope of a mixed pre-stranding — see helpers/phantomRopeSpecs.js.
  // A cable with a reduced earth is stranded twice, at two different sections,
  // and `/phantoms/compatible` matches pre-stranding stock BY SECTION. Asking
  // once with the main section is why the 16 mm² reels were never offered.
  ropeSpecs = null,
  onUsePhantom,
  onUsePhantoms,
  usedPhantomIds = [],
  // Metres claimed by OTHER products/phases in this wizard, per reel.
  phantomAllocMap = {},
  // Metres this very product+phase has committed, per reel.
  ownAllocMap = {},
  // { phantomId: [{ label, metres, own }] } — who is on the reel, for the card.
  phantomTakenBy = {}
}) => {
  const params = useMemo(() => {
    const p = { phase: phaseName };
    if (section) p.section = section;

    // for pre_stranding, pass wire specs from phase data for precise matching
    if (phaseName === 'pre_stranding' && phaseData) {
      if (phaseData.type) p.wireType = phaseData.type;
      if (phaseData.wireDiameter) p.wireDiameter = JSON.stringify(phaseData.wireDiameter);
      if (phaseData.numberOfWires) p.numberOfWires = JSON.stringify(phaseData.numberOfWires);
    }

    return p;
  }, [phaseName, section, phaseData]);

  // One query per rope, so each rope asks for its OWN section. The spec that
  // asked is remembered on every reel it brought back: that is how a selection
  // knows which rope's execution it becomes, and it is more reliable than
  // matching the reel back to a rope by wire diameter afterwards — the endpoint
  // ignores diameters on purpose.
  const perRope = useMemo(() => {
    if (phaseName !== 'pre_stranding') return [];
    return (ropeSpecs || []).filter(s => s && s.ropeIndex != null);
  }, [phaseName, ropeSpecs]);

  const ropeQueries = useQueries({
    queries: perRope.map(spec => {
      const p = { phase: 'pre_stranding' };
      if (spec.section) p.section = spec.section;
      if (spec.wireType) p.wireType = spec.wireType;
      return {
        queryKey: ['phantoms', 'compatible', p],
        queryFn: () => fetchCompatible(p),
        refetchInterval: 15000,
        staleTime: 10000
      };
    })
  });

  const singleQuery = useQuery({
    queryKey: ['phantoms', 'compatible', params],
    queryFn: () => fetchCompatible(params),
    enabled: !!phaseName && perRope.length === 0,
    refetchInterval: 15000,
    staleTime: 10000
  });

  const { phantoms, isLoading } = useMemo(() => {
    if (!perRope.length) {
      return { phantoms: singleQuery.data || [], isLoading: singleQuery.isLoading };
    }

    // A reel that fits two ropes is shown under the first that asked for it:
    // one physical reel cannot feed both, and offering it twice would let the
    // same metres be spent twice over.
    const seen = new Set();
    const out = [];
    perRope.forEach((spec, i) => {
      for (const ph of ropeQueries[i]?.data || []) {
        if (seen.has(ph.phantomId)) continue;
        seen.add(ph.phantomId);
        out.push({ ...ph, ropeIndex: spec.ropeIndex, ropeLabel: spec.label, ropeStructure: spec.structure });
      }
    });

    return { phantoms: out, isLoading: ropeQueries.some(q => q.isLoading) };
  }, [perRope, ropeQueries, singleQuery.data, singleQuery.isLoading]);

  const available = useMemo(() => {
    return phantoms
      .map(p => {
        // What OTHER products and phases in this wizard have already claimed.
        // This phase's own claim is deliberately NOT subtracted: confirming
        // replaces every phantom execution on the phase, so its own metres are
        // still its to spend. Subtracting them is what made a reel the planner
        // had just used read "13.5 m available" with no way back.
        const allocated = phantomAllocMap[p.phantomId] || 0;
        const effectiveAvailable = Math.max(0, p.availableLength - allocated);
        return { ...p, effectiveAvailable };
      })
      .filter(p => {
        // A reel with nothing left is dropped — unless this phase is the one
        // holding it, in which case it has to stay on screen to be adjusted.
        if (p.effectiveAvailable <= 0 && !(ownAllocMap[p.phantomId] > 0)) return false;
        if (phaseName === 'conductor_isolation' && wireSpecs?.length) {
          return isWireCompatible(p, wireSpecs);
        }
        return true;
      });
  }, [phantoms, phaseName, wireSpecs, phantomAllocMap, ownAllocMap]);

  if (isLoading) {
    return (
      <div className="text-muted fs-10 py-1">
        <Spinner animation="border" size="sm" className="me-1" />
        Checking stock...
      </div>
    );
  }

  if (!available.length) return null;

  const isMultiSelect = phaseName === 'pre_stranding';

  return (
    <Card className="mb-3 border-0 shadow-sm" style={{ borderLeft: '5px solid #0dcaf0' }}>
      <Card.Header className="bg-info-subtle fw-semibold py-2 d-flex justify-content-between align-items-center">
        <span>
          {isMultiSelect ? 'Available Stock Reels' : 'Available Phantom Tokens'}
          <Badge bg="info" className="ms-2">{available.length}</Badge>
        </span>
        <span className="text-muted fs-10">
          {isMultiSelect ? 'Select reels and quantities to use from stock' : 'Click to use as input'}
        </span>
      </Card.Header>
      <Card.Body className="pt-2 pb-2">
        {isMultiSelect ? (
          <MultiSelectPicker
            available={available}
            totalNeeded={totalNeeded || 0}
            onUsePhantoms={onUsePhantoms || (() => {})}
            usedPhantomIds={usedPhantomIds}
            ropeSpecs={perRope.length ? perRope : ropeSpecs}
            ownAlloc={ownAllocMap}
            takenBy={phantomTakenBy}
          />
        ) : (
          <Row className="g-2">
            {available.map(ph => (
              <Col xs={12} sm={6} md={4} key={ph.phantomId}>
                <SingleSelectCard phantom={ph} onUsePhantom={onUsePhantom} />
              </Col>
            ))}
          </Row>
        )}
      </Card.Body>
    </Card>
  );
};

export default PhantomTokenPicker;
